import { forwardRef } from 'react'
import type * as Y from 'yjs'
import {
  DEFAULT_THEME,
  type BoardElement,
  type BoardTheme,
  type FontDto,
  type Frame,
  type MediaDto,
} from '@praesto/shared'
import type { RemotePresence } from '../collab/usePresence.js'
import type { CameraStore } from './CameraStore.js'
import { ElementView, type ResizeCorner } from './ElementView.js'
import { FrameView } from './FrameView.js'
import { Cursors } from './Cursors.js'
import { isSelected, selectionSize, type Selection } from './selection.js'

export interface LayerInteractions {
  /** `additive` bei gedrückter Umschalttaste: hinzunehmen statt ersetzen. */
  onSelectFrame: (id: string, additive: boolean) => void
  onZoomToFrame: (frame: Frame) => void
  onElementDragStart: (event: React.PointerEvent, element: BoardElement) => void
  onElementResizeStart: (
    event: React.PointerEvent,
    element: BoardElement,
    corner: ResizeCorner,
  ) => void
  onFrameDragStart: (event: React.PointerEvent, frame: Frame) => void
  onFrameResizeStart: (event: React.PointerEvent, frame: Frame, corner: ResizeCorner) => void
  onEditIframeUrl: (id: string) => void
  /** Holt das Yjs-Fragment eines Textelements — erst wenn es bearbeitet wird. */
  onEditText: (id: string) => Promise<Y.XmlFragment | null>
  onPatchElement: (id: string, patch: Partial<BoardElement>) => void
  onPatchFrame: (id: string, patch: Partial<Frame>) => void
  /** Fehlt, wo nicht bearbeitet wird — dann gibt es auch keine Vorlage. */
  onSaveFrameTemplate?: (frame: Frame) => void
  /**
   * Nimmt ein Objekt beim Größenändern seinen Inhalt mit?
   *
   * Für einen Frame heißt das: seine Elemente. Für ein Textfeld: seine
   * Schriftgröße. Bewusst dieselbe Einstellung, weil es dieselbe Frage ist —
   * ziehe ich einen Rahmen auf oder vergrößere ich, was darin steht.
   *
   * Ein Werkzeugmodus, kein Merkmal des Objekts — deshalb reicht er hier durch
   * und liegt nicht im Dokument. Fehlt in Ansichten ohne Werkzeuge.
   */
  scaleContent?: boolean
  onToggleScaleContent?: () => void
}

/** Für nicht bedienbare Ansichten wie die Gast-Vorschau. */
export const READ_ONLY_INTERACTIONS: LayerInteractions = {
  onSelectFrame: () => {},
  onZoomToFrame: () => {},
  onElementDragStart: () => {},
  onElementResizeStart: () => {},
  onFrameDragStart: () => {},
  onFrameResizeStart: () => {},
  onEditIframeUrl: () => {},
  onEditText: () => Promise.resolve(null),
  onPatchElement: () => {},
  onPatchFrame: () => {},
}

export interface BoardLayerProps {
  camera: CameraStore
  elements: readonly BoardElement[]
  frames: readonly Frame[]
  mediaById: Map<string, MediaDto>
  presence?: readonly RemotePresence[]
  selection: Selection
  canEdit: boolean
  /** Darf die eingebettete Seite bedient werden? In der Vorschau nie. */
  allowIframeInteraction?: boolean
  /** Schreibrecht am Dokument — im Vortrag weiterhin gegeben. */
  canWrite?: boolean
  theme?: BoardTheme
  /**
   * Selbst gehostete Schriften der Organisation. Nur die Textleiste braucht
   * sie; angezeigt wird jede Schrift ohnehin über das Stylesheet der
   * Organisation.
   */
  fonts?: readonly FontDto[]
  interactions?: LayerInteractions
  /** Zusätzliche Ebenen im Board-Koordinatensystem, etwa Hilfslinien. */
  children?: React.ReactNode
}

/**
 * Der transformierte Inhalt eines Boards.
 *
 * Bewusst von der Eingabebehandlung getrennt, weil dieselbe Darstellung an zwei
 * Stellen gebraucht wird: im bedienbaren Canvas und in der Gast-Vorschau des
 * Präsentierenden. Die Vorschau ist damit kein Screensharing, sondern eine
 * zweite Ansicht desselben Dokuments mit fremder Kamera.
 */
export const BoardLayer = forwardRef<HTMLDivElement, BoardLayerProps>(function BoardLayer(
  {
    camera,
    elements,
    frames,
    mediaById,
    presence,
    selection,
    canEdit,
    allowIframeInteraction = false,
    canWrite = false,
    theme = DEFAULT_THEME,
    fonts = [],
    interactions = READ_ONLY_INTERACTIONS,
    children,
  },
  ref,
) {
  // Vieles gilt nur, wenn genau ein Objekt ausgewählt ist — Anfasser,
  // Gestaltungsleisten, Texteingabe.
  const single = selectionSize(selection) === 1

  return (
    <div ref={ref} className="canvas-layer">
      {/* Frames liegen hinter den Elementen — sie rahmen Inhalte, sie verdecken sie nicht. */}
      {frames.map((frame, index) => (
        <FrameView
          key={frame.id}
          frame={frame}
          position={index + 1}
          camera={camera}
          selected={isSelected(selection, 'frame', frame.id)}
          sole={single && isSelected(selection, 'frame', frame.id)}
          showChrome={canEdit}
          onSelect={interactions.onSelectFrame}
          onZoomTo={interactions.onZoomToFrame}
          onDragStart={interactions.onFrameDragStart}
          onResizeStart={interactions.onFrameResizeStart}
          onPatch={interactions.onPatchFrame}
          onSaveTemplate={interactions.onSaveFrameTemplate}
          scaleContent={interactions.scaleContent ?? false}
          onToggleScaleContent={interactions.onToggleScaleContent}
          theme={theme}
        />
      ))}

      {elements.map((element) => (
        <ElementView
          key={element.id}
          element={element}
          media={'mediaId' in element ? mediaById.get(element.mediaId) : undefined}
          camera={camera}
          selected={isSelected(selection, 'element', element.id)}
          sole={single && isSelected(selection, 'element', element.id)}
          canEdit={canEdit}
          onDragStart={interactions.onElementDragStart}
          onResizeStart={interactions.onElementResizeStart}
          allowIframeInteraction={allowIframeInteraction}
          canWrite={canWrite}
          theme={theme}
          fonts={fonts}
          scaleFont={interactions.scaleContent ?? false}
          onToggleScaleFont={interactions.onToggleScaleContent}
          onEditIframeUrl={interactions.onEditIframeUrl}
          onEditText={interactions.onEditText}
          onPatchElement={interactions.onPatchElement}
        />
      ))}

      {presence && <Cursors presence={presence} />}
      {children}
    </div>
  )
})
